import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useEffect, useState, useContext } from "react";
import { WalletProviderContext } from "../walletprovider/WalletProvider";
import { ethers } from "ethers";
import { avalancheDecrypt } from "./avalancheEncryption";
import { dwReportError } from "../utils/diagnostics/clientErrors";
import { avalancheServerDecrypt, avalancheServerWrite } from "./avalancheServerSigner";
const CONTRACT_ADDRESS = process.env.CONTRACT_ADDRESS || "";
// Your ABI Here
const ABI = [
    {
        inputs: [],
        name: "get",
        outputs: [
            {
                internalType: "string",
                name: "",
                type: "string",
            },
        ],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [
            {
                internalType: "string",
                name: "value",
                type: "string",
            },
        ],
        name: "set",
        outputs: [],
        stateMutability: "nonpayable",
        type: "function",
    },
    {
        inputs: [],
        name: "hasData",
        outputs: [
            {
                internalType: "bool",
                name: "",
                type: "bool",
            },
        ],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [
            {
                internalType: "address",
                name: "addr",
                type: "address",
            },
        ],
        name: "getDataForAddress",
        outputs: [
            {
                internalType: "string",
                name: "",
                type: "string",
            },
        ],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [
            {
                internalType: "address",
                name: "addr",
                type: "address",
            },
            {
                internalType: "string",
                name: "value",
                type: "string",
            },
        ],
        name: "setDataForAddress",
        outputs: [],
        stateMutability: "nonpayable",
        type: "function",
    },
    {
        inputs: [
            {
                internalType: "address",
                name: "addr",
                type: "address",
            },
        ],
        name: "hasDataForAddress",
        outputs: [
            {
                internalType: "bool",
                name: "",
                type: "bool",
            },
        ],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [],
        name: "getAllData",
        outputs: [
            {
                internalType: "string[]",
                name: "",
                type: "string[]",
            },
        ],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [
            {
                internalType: "string",
                name: "key",
                type: "string",
            },
            {
                internalType: "string",
                name: "value",
                type: "string",
            },
        ],
        name: "setFeedbackData",
        outputs: [],
        stateMutability: "nonpayable",
        type: "function",
    },
    {
        inputs: [],
        name: "getAllFeedback",
        outputs: [
            {
                internalType: "string[]",
                name: "",
                type: "string[]",
            },
        ],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [],
        name: "deleteAllFeedback",
        outputs: [],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [
            {
                internalType: "address",
                name: "key",
                type: "address",
            },
            {
                internalType: "string",
                name: "value",
                type: "string",
            },
        ],
        name: "setUserAddress",
        outputs: [],
        stateMutability: "nonpayable",
        type: "function",
    },
    {
        inputs: [],
        name: "getAllUserAddress",
        outputs: [
            {
                internalType: "string[]",
                name: "",
                type: "string[]",
            },
        ],
        stateMutability: "view",
        type: "function",
    },
];
const FUJI_RPC_URL = "https://api.avax-test.network/ext/bc/C/rpc";
// When AES-CBC is enabled, the key/IV are server-only secrets: plaintext is
// sent to the orchestrator which encrypts before signing the transaction
// (/avalanche/write), and ciphertext read from the chain is decrypted via
// /avalanche/decrypt. When disabled, values are stored as-is (pass-through).
const AES_CBC_ENABLED = process.env.AES_CBC_ENABLED === "true";
export const AvalancheContext = createContext({
    get: () => Promise.reject(undefined),
    set: (x) => Promise.reject(),
    hasData: () => Promise.resolve(false),
    getDataForAddress: (key) => Promise.reject(undefined),
    setDataForAddress: (key, y) => Promise.reject(),
    hasDataForAddress: (key) => Promise.resolve(false),
    setFeedbackData: (key, x) => Promise.reject(),
    getAllFeedback: () => Promise.reject(undefined),
    deleteAllFeedback: () => Promise.reject(),
    setUserAddress: (key, x) => Promise.reject(),
    getAllUserAddress: () => Promise.reject(undefined),
});
export const AvalancheProvider = ({ children }) => {
    const { selectedWallet, selectedAccount } = useContext(WalletProviderContext);
    const provider = new ethers.providers.JsonRpcProvider(FUJI_RPC_URL);
    const [dwinityStorage, setDwinityStorage] = useState();
    useEffect(() => {
        //console.log("AvalancheProvider useEffect");
        setDwinityStorage(new ethers.Contract(CONTRACT_ADDRESS, ABI, provider));
    }, []);
    // NOTE: the auth signer for server-side writes is registered in
    // WalletProvider (mounted at the app root) — see setAvalancheWriteAuthSigner.
    // Decrypt a stored value: pass-through client-side when AES is disabled,
    // otherwise delegate to the orchestrator (the key/IV never reach the browser).
    const decryptStored = async (encrypted) => {
        if (!AES_CBC_ENABLED)
            return avalancheDecrypt(encrypted);
        const [value] = await avalancheServerDecrypt([encrypted]);
        return value ?? undefined;
    };
    const get = async () => {
        const encrypted = await dwinityStorage?.get();
        if (encrypted !== undefined) {
            try {
                return await decryptStored(encrypted);
            }
            catch (error) {
                dwReportError(selectedAccount ?? "N/A", undefined, error, { encrypted: encrypted });
                return undefined;
            }
        }
        else {
            return undefined;
        }
    };
    // NOTE: plaintext is sent to the orchestrator, which encrypts server-side
    // when AES_CBC_ENABLED=true (and fails loudly with "encryption not
    // configured" when the keys are missing) — see /avalanche/write.
    const set = async (t) => {
        await avalancheServerWrite("storage", "set", [t]);
    };
    const getDataForAddress = async (key) => {
        const encrypted = await dwinityStorage?.getDataForAddress(key);
        if (encrypted !== undefined) {
            try {
                return await decryptStored(encrypted);
            }
            catch (error) {
                dwReportError(selectedAccount ?? "N/A", undefined, error, { encrypted: encrypted });
                return undefined;
            }
        }
        else {
            return undefined;
        }
    };
    const setDataForAddress = async (key, value) => {
        await avalancheServerWrite("storage", "setDataForAddress", [key, value]);
    };
    const setFeedbackData = async (key, value) => {
        await avalancheServerWrite("storage", "setFeedbackData", [key, value]);
    };
    const getAllFeedback = async () => {
        const encryptedArray = await dwinityStorage?.getAllFeedback();
        if (encryptedArray !== undefined) {
            const entries = encryptedArray;
            if (AES_CBC_ENABLED) {
                try {
                    const values = await avalancheServerDecrypt(entries);
                    return values.map((v) => v ?? "");
                }
                catch (error) {
                    dwReportError(selectedAccount ?? "N/A", undefined, error, { count: entries.length });
                    return undefined;
                }
            }
            const decryptedArray = [];
            for (const enc of entries) {
                try {
                    decryptedArray.push(avalancheDecrypt(enc) ?? "");
                }
                catch (error) {
                    dwReportError(selectedAccount ?? "N/A", undefined, error, { encrypted: enc });
                }
            }
            return decryptedArray;
        }
        else {
            return undefined;
        }
    };
    const setUserAddress = async (key, value) => {
        await avalancheServerWrite("storage", "setUserAddress", [key, value]);
    };
    const deleteAllFeedback = async () => {
        await avalancheServerWrite("storage", "deleteAllFeedback", []);
    };
    const getAllUserAddress = async () => {
        const encryptedArray = await dwinityStorage?.getAllUserAddress();
        if (encryptedArray !== undefined) {
            const entries = encryptedArray;
            if (!AES_CBC_ENABLED)
                return entries;
            try {
                const values = await avalancheServerDecrypt(entries);
                return values.map((v) => v ?? "");
            }
            catch (error) {
                dwReportError(selectedAccount ?? "N/A", undefined, error, { count: entries.length });
                return undefined;
            }
        }
        else {
            return undefined;
        }
    };
    const hasDataForAddress = async (key) => {
        const hasData = await dwinityStorage?.hasDataForAddress(key);
        return hasData;
    };
    const contextValue = {
        get: get,
        set: set,
        hasData: dwinityStorage?.hasData,
        getDataForAddress: getDataForAddress,
        setDataForAddress: setDataForAddress,
        hasDataForAddress: hasDataForAddress,
        setFeedbackData: setFeedbackData,
        getAllFeedback: getAllFeedback,
        deleteAllFeedback: deleteAllFeedback,
        setUserAddress: setUserAddress,
        getAllUserAddress: getAllUserAddress,
    };
    return _jsx(AvalancheContext.Provider, { value: contextValue, children: children });
};
export const useAvalancheProvider = () => useContext(AvalancheContext);
